import CartIndicator from "~/components/cart/Indicator"
import { sendEvent } from "~/sdk/analytics"
import { getCart, type OrderForm } from "~/hooks/useCart"
import { useSignal } from "~/sdk/signal"
import { toAnalytics } from "~/sdk/ga4/transform/toAnalytics"
import { getCartItemVariants, orderFormItemToProductItem } from "~/sdk/ga4/transform/itemVariant"
import Icon from "~/components/ui/Icon"
import { lazy, Suspense } from "react"
import { type Props } from "~/components/cart/Cart"

// Lazy-mount the header Cart only after the user actually interacts with
// it. Previously it was always rendered (group-hover via CSS just hid it),
// which meant Cart + N CartItems + their useCart/useSignal/useRef cascades
// ran on every page load — for a logged-in user with several items in cart
// that meant thousands of nodes and effects firing on the initial paint of
// every route.
const Cart = lazy(() => import("~/components/cart/Cart"))

async function buildViewCartEvent(cart: OrderForm | null) {
    const cartItems = cart?.items ?? []
    const variants = await getCartItemVariants(cartItems)

    return toAnalytics({
        type: 'view_cart',
        data: {
            items: cartItems.map((item, index) =>
                orderFormItemToProductItem(
                    item,
                    { index, quantity: item.quantity },
                    variants[item.id],
                )
            ),
            view: { id: 'minicart', name: 'Carrinho' },
        },
    })
}

// `Partial<Props>`: o header não pode depender de o bloco de CMS ter
// `freeShippingValue` configurado. Sem ele, o dropdown (única coisa que usa o
// valor, via barra de frete grátis) não monta; o link da sacola e o contador
// continuam funcionando.
export default function Button(props: Partial<Props>) {
    // Non-reactive: only read inside onMouseEnter to build the view_cart
    // analytics payload. We don't render anything that depends on the cart
    // here — Cart is lazy-mounted on hover. Calling useCart() would register
    // a listener for nothing.
    const { cart } = getCart()
    const mounted = useSignal(false)

    function trigger() {
        mounted.value = true
        // Assíncrono: o rótulo da variação (item_variant) vem do catálogo.
        void buildViewCartEvent(cart?.value ?? null).then(sendEvent)
    }

    return (
        <div
            id='header-cart-hover'
            className='relative flex flex-col items-center justify-center gap-1 group z-[3]'
            onMouseEnter={trigger}
            onTouchStart={trigger}
        >
            <a
                href='/checkout/#/cart'
                className='relative z-[2]'
                rel='nofollow'
            >
                {/* Nome acessível composto pelo conteúdo ("Sacola 3") em vez de
                    um aria-label, que substituiria o badge de quantidade. */}
                <span className='sr-only'>Sacola</span>
                <Icon id='Sacola' size={36} className='text-orange' />

                <div className='absolute -bottom-[10%] -right-[15%]'>
                    <CartIndicator />
                </div>
            </a>

            <span className='text-xs font-bold'>Minhas Compras</span>

            {mounted.value && props.freeShippingValue !== undefined && (
                <Suspense fallback={null}>
                    <Cart {...(props as Props)} />
                </Suspense>
            )}
        </div>
    )
}
